import { cn } from '@/lib/cn';
import { format, parse } from 'date-fns';
import { Calendar } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import { Card } from '../dashboard/DashboardCard';
import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';

export function matchesFilterByNameAndEmail(
  search: string,
  name: string,
  email: string,
): boolean {
  const trimmed = search.trim();
  if (!trimmed) return true;
  const q = trimmed.toLowerCase();
  const nameLower = name.toLowerCase();
  const emailLower = email.toLowerCase();
  return nameLower.includes(q) || emailLower.includes(q);
}

export function sortByNameAndEmail<T>(
  items: Array<T>,
  getName: (item: T) => string,
  getEmail: (item: T) => string = () => '',
): Array<T> {
  return [...items].sort((a, b) => {
    const nameA = getName(a).toLowerCase();
    const nameB = getName(b).toLowerCase();
    if (nameA !== nameB) return nameA.localeCompare(nameB);
    const emailA = getEmail(a).toLowerCase();
    const emailB = getEmail(b).toLowerCase();
    return emailA.localeCompare(emailB);
  });
}

export const FilterOptionButton = ({
  label,
  isActive,
  onClick,
}: {
  label: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={cn(
      'h-15 rounded-lg px-6 py-2.5 text-base font-normal transition-all',
      isActive
        ? 'bg-[#213E73] text-white'
        : 'text-table-foreground bg-[#F1F3F5] hover:bg-[#E2E6EA]',
    )}
  >
    {label}
  </button>
);

export const FilterButton = ({
  isPending,
  label,
  onClick,
}: {
  isPending: boolean;
  label: string;
  onClick: () => void;
}) => {
  return (
    <Button
      type="submit"
      className="h-15 rounded-xl px-8 whitespace-nowrap"
      disabled={isPending}
      onClick={onClick}
    >
      {label}
    </Button>
  );
};

interface DateRangePickerButtonProps {
  dateFrom: string;
  dateTo: string;
  onDateRangeChange: (from: string, to: string) => void;
  isActive?: boolean;
}

const parseDate = (dateStr: string): Date | undefined => {
  if (!dateStr) return undefined;
  try {
    return parse(dateStr, 'yyyy-MM-dd', new Date());
  } catch {
    return undefined;
  }
};

const formatDate = (date: Date | undefined): string => {
  if (!date) return '';
  return format(date, 'yyyy-MM-dd');
};

const calendarClassNames = {
  root: 'relative',
  month: 'flex flex-col gap-4',
  month_caption: 'flex justify-center items-center h-10 mb-2',
  caption_label: 'hidden',
  dropdowns: 'relative z-10 flex gap-2 items-center justify-center',
  dropdown:
    'relative z-20 bg-background border border-input rounded-md px-2 py-1 text-xs font-medium text-table-foreground cursor-pointer hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50',
  months_dropdown: 'min-w-[80px]',
  years_dropdown: 'min-w-[60px]',
  month_grid: 'w-full border-collapse',
  weekdays: 'flex',
  weekday: 'text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]',
  week: 'flex w-full mt-2',
  day: 'h-9 w-9 text-center text-sm p-0 relative',
  day_button:
    'h-9 w-9 p-0 font-normal hover:bg-primary/20 rounded-md transition-colors',
  range_start: 'bg-primary text-primary-foreground rounded-l-md',
  range_end: 'bg-primary text-primary-foreground rounded-r-md',
  selected: 'bg-primary text-primary-foreground rounded-md',
  today: 'font-bold',
  disabled: 'text-muted-foreground opacity-50',
  range_middle: 'bg-primary/20 text-table-foreground',
  hidden: 'invisible',
};

export const DateRangePickerButton = ({
  dateFrom,
  dateTo,
  onDateRangeChange,
  isActive = false,
}: DateRangePickerButtonProps) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [leftMonth, setLeftMonth] = useState<Date>(new Date());
  const [rightMonth, setRightMonth] = useState<Date>(
    new Date(new Date().getFullYear(), new Date().getMonth() + 1),
  );

  const handleLeftMonthChange = (newMonth: Date) => {
    const prevYear = leftMonth.getFullYear();
    const newYear = newMonth.getFullYear();
    setLeftMonth(newMonth);
    if (prevYear !== newYear && range?.from) {
      const updatedFrom = new Date(range.from);
      updatedFrom.setFullYear(newYear);
      if (range.to && updatedFrom > range.to) {
        setRange({ from: updatedFrom, to: undefined });
      } else {
        setRange((prev) => (prev ? { ...prev, from: updatedFrom } : prev));
      }
    }
  };

  const handleRightMonthChange = (newMonth: Date) => {
    const prevYear = rightMonth.getFullYear();
    const newYear = newMonth.getFullYear();
    setRightMonth(newMonth);
    if (prevYear !== newYear && range?.to) {
      const updatedTo = new Date(range.to);
      updatedTo.setFullYear(newYear);
      if (range.from && updatedTo < range.from) {
        setRange({ from: undefined, to: updatedTo });
      } else {
        setRange((prev) => (prev ? { ...prev, to: updatedTo } : prev));
      }
    }
  };

  const handleOpen = () => {
    if (isActive) {
      const fromDate = parseDate(dateFrom);
      const toDate = parseDate(dateTo);
      setRange({ from: fromDate, to: toDate });
      if (fromDate) setLeftMonth(fromDate);
      if (toDate) setRightMonth(toDate);
    } else {
      setRange(undefined);
      setLeftMonth(new Date());
      setRightMonth(
        new Date(new Date().getFullYear(), new Date().getMonth() + 1),
      );
    }
    setOpen(true);
  };

  const handleApply = () => {
    onDateRangeChange(formatDate(range?.from), formatDate(range?.to));
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const displayText = isActive
    ? `${dateFrom} - ${dateTo}`
    : '2000-01-01 - 2099-12-31';

  return (
    <>
      <button
        onClick={handleOpen}
        className={cn(
          'flex h-15 items-center gap-2 rounded-lg px-6 py-2.5 text-base font-normal transition-all',
          isActive
            ? 'bg-[#213E73] text-white'
            : 'text-table-foreground bg-[#F1F3F5] hover:bg-[#E2E6EA]',
        )}
      >
        <Calendar className="size-4" />
        {displayText}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          variant="default"
          showCloseButton={false}
          className="bg-form-linear p-6"
        >
          <Card className="h-full w-full overflow-y-auto p-6 shadow-none md:max-h-[70vh]">
            <DialogHeader className="mb-6 text-center">
              <DialogTitle className="font-light">
                Select date range
              </DialogTitle>
            </DialogHeader>

            <div className="flex flex-col gap-8 px-4 py-4 sm:flex-row">
              <div>
                <div className="text-muted-foreground mb-2 text-center text-xs font-medium">
                  Start Date
                </div>
                <DayPicker
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  month={leftMonth}
                  onMonthChange={handleLeftMonthChange}
                  disabled={range?.to ? { after: range.to } : undefined}
                  showOutsideDays={false}
                  captionLayout="dropdown"
                  startMonth={new Date(2000, 0)}
                  endMonth={new Date(2099, 11)}
                  hideNavigation
                  classNames={calendarClassNames}
                />
              </div>

              <div>
                <div className="text-muted-foreground mb-2 text-center text-xs font-medium">
                  End Date
                </div>
                <DayPicker
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  month={rightMonth}
                  onMonthChange={handleRightMonthChange}
                  disabled={range?.from ? { before: range.from } : undefined}
                  showOutsideDays={false}
                  captionLayout="dropdown"
                  startMonth={new Date(2000, 0)}
                  endMonth={new Date(2099, 11)}
                  hideNavigation
                  classNames={calendarClassNames}
                />
              </div>
            </div>

            <div className="text-muted-foreground mb-4 text-center text-sm">
              {range?.from && range.to ? (
                <>
                  Selected: {format(range.from, 'MMM dd, yyyy')} -{' '}
                  {format(range.to, 'MMM dd, yyyy')}
                </>
              ) : range?.from ? (
                <>Select end date</>
              ) : (
                <>Click to select start date</>
              )}
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outlinePrimary"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleApply}
                disabled={!range?.from || !range.to}
              >
                Apply
              </Button>
            </DialogFooter>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
};
